import React, { useEffect, useState } from 'react';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, BarChart3, Mail, MousePointerClick, Send, TrendingUp, Users } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { crmChartOptions, crmDoughnutOptions } from '@/lib/chartTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

function formatCLP(n: number) {
  return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(n || 0);
}

export default function ReportsPage() {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [sales, setSales] = useState<any>(null);
  const [performance, setPerformance] = useState<any[]>([]);
  const { currentOrgId } = useOrgStore();

  useEffect(() => {
    if (!currentOrgId) return;
    api.get('/reports/campaigns').then((response) => setCampaigns(response.data || [])).catch(console.error);
    api.get('/reports/sales').then((response) => setSales(response.data)).catch(console.error);
    api.get('/reports/performance').then((response) => setPerformance(response.data || [])).catch(console.error);
  }, [currentOrgId]);

  const totalEmails = campaigns.reduce((sum, item) => sum + Number(item.emails_sent || 0), 0);
  const totalOpened = campaigns.reduce((sum, item) => sum + Number(item.opened || 0), 0);
  const totalClicked = campaigns.reduce((sum, item) => sum + Number(item.clicked || 0), 0);
  const totalConversions = campaigns.reduce((sum, item) => sum + Number(item.conversions || 0), 0);

  const salesMetrics = sales ? [
    { label: 'Ingresos ganados', value: formatCLP(sales.total_revenue), icon: TrendingUp, color: '#22C55E', bg: '#F0FDF4' },
    { label: 'Pipeline total', value: formatCLP(sales.pipeline_total), icon: BarChart3, color: '#2563EB', bg: '#EFF6FF' },
    { label: 'Pipeline ponderado', value: formatCLP(sales.weighted_pipeline), icon: TrendingUp, color: '#8B5CF6', bg: '#F5F3FF' },
    { label: 'Forecast mensual', value: formatCLP(sales.monthly_forecast), icon: TrendingUp, color: '#0EA5E9', bg: '#ECFEFF' },
    { label: 'Ganadas', value: sales.won_count, icon: Users, color: '#22C55E', bg: '#F0FDF4' },
    { label: 'Perdidas', value: sales.lost_count, icon: AlertTriangle, color: '#DC2626', bg: '#FEF2F2' },
  ] : [];

  const salesChartData = sales?.by_stage ? {
    labels: sales.by_stage.map((item: any) => item._id),
    datasets: [{
      label: 'Monto (CLP)',
      data: sales.by_stage.map((item: any) => item.total),
      backgroundColor: ['#94A3B8', '#2563EB', '#0EA5E9', '#F59E0B', '#8B5CF6', '#22C55E', '#EF4444'],
      borderRadius: 8,
      borderSkipped: false,
    }],
  } : null;

  const performanceChartData = performance.length ? {
    labels: performance.map((item) => item.seller_name),
    datasets: [
      {
        label: 'Monto vendido',
        data: performance.map((item) => item.amount_sold),
        backgroundColor: '#2563EB',
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Ventas cerradas',
        data: performance.map((item) => item.closed_won_count),
        backgroundColor: '#22C55E',
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  } : null;

  const lostReasonsChartData = sales?.lost_reasons?.length ? {
    labels: sales.lost_reasons.map((item: any) => item._id || 'Sin motivo'),
    datasets: [{
      data: sales.lost_reasons.map((item: any) => item.count),
      backgroundColor: ['#EF4444', '#F59E0B', '#8B5CF6', '#2563EB', '#22C55E', '#94A3B8'],
      borderWidth: 0,
    }],
  } : null;

  const campaignChartData = campaigns.length > 0 ? {
    labels: campaigns.map((item) => item.name?.substring(0, 20)),
    datasets: [
      { label: 'Enviados', data: campaigns.map((item) => item.emails_sent), backgroundColor: '#2563EB', borderRadius: 8, borderSkipped: false },
      { label: 'Abiertos', data: campaigns.map((item) => item.opened), backgroundColor: '#22C55E', borderRadius: 8, borderSkipped: false },
      { label: 'Clicks', data: campaigns.map((item) => item.clicked), backgroundColor: '#F59E0B', borderRadius: 8, borderSkipped: false },
    ],
  } : null;

  return (
    <div data-testid="reports-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Reportes</h1>
        <p className="text-sm text-[#475569] mt-1">Ventas, forecast, rendimiento por vendedor y marketing secundario.</p>
      </div>

      <Tabs defaultValue="sales" className="space-y-4">
        <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          <TabsList className="flex h-auto w-max min-w-full justify-start bg-[#F1F5F9] sm:w-auto sm:min-w-0">
            <TabsTrigger value="sales" data-testid="tab-sales" className="min-h-9 whitespace-nowrap">Ventas y rendimiento</TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="tab-campaigns" className="min-h-9 whitespace-nowrap">Marketing</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="sales" className="space-y-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 md:gap-6 xl:grid-cols-6">
            {salesMetrics.map((metric) => {
              const Icon = metric.icon;
              return (
                <Card key={metric.label} className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur overflow-hidden">
                  <CardContent className="p-4 md:p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-1">{metric.label}</p>
                        <p className="text-xl md:text-2xl font-bold tabular-nums text-[#0F172A] truncate">{metric.value}</p>
                      </div>
                      <div className="h-10 w-10 md:h-11 md:w-11 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: metric.bg }}>
                        <Icon size={22} style={{ color: metric.color }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur">
              <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Pipeline por etapa</CardTitle></CardHeader>
              <CardContent><div className="h-72">{salesChartData ? <Bar data={salesChartData} options={{ ...crmChartOptions, plugins: { ...crmChartOptions.plugins, legend: { display: false } } }} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Cargando...</div>}</div></CardContent>
            </Card>
            <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur">
              <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Motivos de pérdida</CardTitle></CardHeader>
              <CardContent><div className="h-72">{lostReasonsChartData ? <Doughnut data={lostReasonsChartData} options={crmDoughnutOptions} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Sin datos suficientes</div>}</div></CardContent>
            </Card>
          </div>

          <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur">
            <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Performance por vendedor</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="h-72">{performanceChartData ? <Bar data={performanceChartData} options={crmChartOptions} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Cargando...</div>}</div>

              {/* Desktop: tabla */}
              <div className="hidden lg:block overflow-x-auto rounded-lg border border-[#E2E8F0]">
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B]">Vendedor</th>
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Leads</th>
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Oportunidades</th>
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Ventas</th>
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Conversión</th>
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Monto</th>
                      <th className="py-3 px-4 text-xs font-bold uppercase tracking-wider text-[#64748B] text-right">Actividades</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {performance.map((item) => (
                      <tr key={item.seller_id} className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="py-3 px-4 font-medium text-[#0F172A]">{item.seller_name}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-[#475569]">{item.leads_managed}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-[#475569]">{item.opportunities_created}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-[#475569]">{item.closed_won_count}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-[#475569]">{item.conversion_rate}%</td>
                        <td className="py-3 px-4 text-right tabular-nums text-[#0F172A] font-semibold">{formatCLP(item.amount_sold)}</td>
                        <td className="py-3 px-4 text-right tabular-nums text-[#475569]">{item.activities_done}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Móvil/tablet: tarjetas */}
              <ul className="space-y-3 lg:hidden">
                {performance.map((item) => (
                  <li key={item.seller_id} className="rounded-lg border border-[#E2E8F0] bg-white p-4 shadow-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-[#0F172A]">{item.seller_name}</span>
                      <span className="text-sm font-semibold tabular-nums text-[#0F172A]">{formatCLP(item.amount_sold)}</span>
                    </div>
                    <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <dt className="text-[#64748B]">Leads</dt>
                      <dd className="text-right tabular-nums text-[#334155]">{item.leads_managed}</dd>
                      <dt className="text-[#64748B]">Oportunidades</dt>
                      <dd className="text-right tabular-nums text-[#334155]">{item.opportunities_created}</dd>
                      <dt className="text-[#64748B]">Ventas</dt>
                      <dd className="text-right tabular-nums text-[#334155]">{item.closed_won_count}</dd>
                      <dt className="text-[#64748B]">Conversión</dt>
                      <dd className="text-right tabular-nums text-[#334155]">{item.conversion_rate}%</dd>
                      <dt className="text-[#64748B]">Actividades</dt>
                      <dd className="text-right tabular-nums text-[#334155]">{item.activities_done}</dd>
                    </dl>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="space-y-4">
          <div className="grid grid-cols-2 gap-4 md:gap-6 xl:grid-cols-4">
            {[
              { label: 'Correos enviados', value: totalEmails.toLocaleString('es-CL'), icon: Send, color: '#2563EB', bg: '#EFF6FF' },
              { label: 'Aperturas', value: totalOpened.toLocaleString('es-CL'), icon: Mail, color: '#22C55E', bg: '#F0FDF4' },
              { label: 'Clicks', value: totalClicked.toLocaleString('es-CL'), icon: MousePointerClick, color: '#F59E0B', bg: '#FFFBEB' },
              { label: 'Conversiones', value: totalConversions.toLocaleString('es-CL'), icon: TrendingUp, color: '#8B5CF6', bg: '#F5F3FF' },
            ].map((metric) => {
              const Icon = metric.icon;
              return (
                <Card key={metric.label} className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur">
                  <CardContent className="p-4 md:p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-bold uppercase tracking-widest text-[#94A3B8] mb-1">{metric.label}</p>
                        <p className="text-xl md:text-2xl font-bold tabular-nums text-[#0F172A] truncate">{metric.value}</p>
                      </div>
                      <div className="h-10 w-10 md:h-11 md:w-11 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: metric.bg }}>
                        <Icon size={22} style={{ color: metric.color }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="border-[#E2E8F0] shadow-sm bg-white/95 backdrop-blur">
            <CardHeader className="pb-2"><CardTitle className="text-base font-semibold text-[#0F172A]">Rendimiento de campañas</CardTitle></CardHeader>
            <CardContent><div className="h-72">{campaignChartData ? <Bar data={campaignChartData} options={crmChartOptions} /> : <div className="flex items-center justify-center h-full text-[#94A3B8]">Cargando...</div>}</div></CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
